import { Injectable } from '@angular/core';
import { AppQualityService } from '../quality/app-quality.service';

export type ExternalNavigationSource = 'universal-link' | 'cold-launch-url' | 'push' | 'web-initial-url' | 'recovered';

interface StoredRoute {
  version: 1 | 2;
  route: string;
  returnRoute?: string;
  savedAt: number;
}

export interface StartupNavigation {
  destination: string;
  fallback: string;
  restored: boolean;
}

interface StoredFeatureState<T> {
  version: 1;
  value: T;
  savedAt: number;
}

interface PendingExternalNavigation {
  version: 1;
  target: string;
  source: ExternalNavigationSource;
  savedAt: number;
  attempts: number;
}

/**
 * Owns only safe, reversible navigation state. Payment, checkout, auth and
 * creation routes are intentionally never restored after process death.
 */
@Injectable({ providedIn: 'root' })
export class AppNavigationStateService {
  private readonly lastRouteKey = 'laniakea.navigation.last-safe-route.v1';
  private readonly pendingExternalKey = 'laniakea.navigation.pending-external.v1';
  private readonly featurePrefix = 'laniakea.navigation.feature.v1:';
  private readonly restoredBackStateKey = 'laniakea.navigation.restore-back.v1';
  private readonly mainNavigationRoute = '/home/ecosystem-page';
  private readonly pendingTtlMs = 10 * 60 * 1000;
  private readonly routeTtlMs = 30 * 24 * 60 * 60 * 1000;
  private readonly recentExternalTargets = new Map<string, number>();
  private readonly inflightExternalTargets = new Map<string, Promise<boolean>>();
  private platformReady = false;
  private platformReadyResolver?: () => void;
  private readonly platformReadyPromise = new Promise<void>(resolve => {
    this.platformReadyResolver = resolve;
  });
  private queueTail: Promise<unknown> = Promise.resolve();
  private startupRouteResolved = false;
  private lastRecordedRoute: string | null = null;

  constructor(private readonly quality: AppQualityService) {}

  markPlatformReady(): void {
    if (this.platformReady) return;
    this.platformReady = true;
    this.platformReadyResolver?.();
    this.platformReadyResolver = undefined;
  }

  recordSuccessfulRoute(rawRoute: string): void {
    const route = this.safeRestorableRoute(rawRoute);
    if (!route) return;

    const stored = this.readStoredRoute();
    const fallback = this.safeBackRoute(
      this.lastRecordedRoute || stored?.route || stored?.returnRoute,
      route,
      true,
    );
    this.write(this.lastRouteKey, {
      version: 2,
      route,
      returnRoute: fallback,
      savedAt: Date.now(),
    } satisfies StoredRoute);
    this.lastRecordedRoute = route;
    this.writeCurrentHistoryBackContext(route, fallback);
  }

  resolveStartupRoute(fallback: string, authenticated: boolean): string {
    return this.resolveStartupNavigation(fallback, authenticated).destination;
  }

  /**
   * Returns enough context to rebuild one safe in-app history entry before a
   * persisted destination is restored. A saved location is only an
   * optimization: its fallback is always a Laniakea route and never itself.
   */
  resolveStartupNavigation(fallback: string, authenticated: boolean): StartupNavigation {
    const safeFallback = this.safeBackRoute(fallback, '', authenticated);
    if (this.startupRouteResolved) {
      return { destination: fallback, fallback: safeFallback, restored: false };
    }
    this.startupRouteResolved = true;
    if (this.hasPendingExternalNavigation()) {
      return { destination: fallback, fallback: safeFallback, restored: false };
    }

    const stored = this.readStoredRoute();
    if (!stored || Date.now() - stored.savedAt > this.routeTtlMs) {
      return { destination: fallback, fallback: safeFallback, restored: false };
    }
    const route = this.safeRestorableRoute(stored.route);
    if (!route || (!authenticated && !this.isPublicRoute(route))) {
      this.discardStartupRoute();
      return { destination: fallback, fallback: safeFallback, restored: false };
    }
    return {
      destination: route,
      fallback: this.safeBackRoute(stored.returnRoute || safeFallback, route, authenticated),
      restored: true,
    };
  }

  /**
   * Returns the deterministic in-app destination for a page's Back action.
   * Components may use this instead of relying on a WebView history entry
   * that a process restart may have discarded.
   */
  backFallbackForCurrentRoute(rawRoute: string): string {
    const route = this.safeRestorableRoute(rawRoute);
    const context = this.currentHistoryBackContext();
    if (route && context?.route === route) {
      return this.safeBackRoute(context.fallback, route, true);
    }
    return this.safeBackRoute('', route || '', true);
  }

  restorationState(destination: string, fallback: string): Record<string, unknown> {
    return {
      [this.restoredBackStateKey]: {
        version: 1,
        route: destination,
        fallback,
      },
    };
  }

  discardStartupRoute(): void {
    this.remove(this.lastRouteKey);
    this.lastRecordedRoute = null;
  }

  saveFeatureState<T>(namespace: string, value: T): void {
    const key = `${this.featurePrefix}${this.safeNamespace(namespace)}`;
    this.write(key, { version: 1, value, savedAt: Date.now() } satisfies StoredFeatureState<T>);
  }

  readFeatureState<T>(namespace: string, maxAgeMs = this.routeTtlMs): T | null {
    const key = `${this.featurePrefix}${this.safeNamespace(namespace)}`;
    const stored = this.read<StoredFeatureState<T>>(key);
    if (!stored || stored.version !== 1 || Date.now() - stored.savedAt > maxAgeMs) {
      this.remove(key);
      return null;
    }
    return stored.value ?? null;
  }

  enqueueExternalNavigation(
    rawTarget: string,
    source: ExternalNavigationSource,
    navigate: (target: string) => Promise<boolean> | boolean,
  ): Promise<boolean> {
    const target = this.safeExternalTarget(rawTarget);
    if (!target) return Promise.resolve(false);

    const recentAt = this.recentExternalTargets.get(target);
    if (recentAt && Date.now() - recentAt < 5000) return Promise.resolve(true);
    const inflight = this.inflightExternalTargets.get(target);
    if (inflight) return inflight;

    this.persistPendingExternal(target, source);
    const run = this.queueTail
      .catch(() => undefined)
      .then(async () => {
        if (!this.platformReady) await this.platformReadyPromise;
        const startedAt = this.now();
        const qualityId = this.quality.beginDeepLink(source, target);
        let success = false;
        try {
          success = await navigate(target);
          if (success) {
            this.recentExternalTargets.set(target, Date.now());
            this.clearPendingExternal(target);
          }
          return success;
        } catch {
          return false;
        } finally {
          this.quality.finishDeepLink(qualityId, source, target, success, startedAt);
          this.pruneRecentTargets();
        }
      });

    this.queueTail = run;
    this.inflightExternalTargets.set(target, run);
    void run.finally(() => this.inflightExternalTargets.delete(target));
    return run;
  }

  recoverPendingExternalNavigation(): { target: string; source: ExternalNavigationSource } | null {
    const pending = this.validPendingExternal();
    if (!pending || pending.attempts >= 3) {
      if (pending?.attempts && pending.attempts >= 3) this.remove(this.pendingExternalKey);
      return null;
    }
    return { target: pending.target, source: 'recovered' };
  }

  hasPendingExternalNavigation(): boolean {
    return this.inflightExternalTargets.size > 0 || !!this.validPendingExternal();
  }

  private persistPendingExternal(target: string, source: ExternalNavigationSource): void {
    if (this.hasSensitiveQuery(target)) {
      // The in-memory navigation still runs, but legal/auth/payment values never
      // enter durable WebView storage.
      this.remove(this.pendingExternalKey);
      return;
    }
    const existing = this.validPendingExternal();
    const attempts = existing?.target === target ? existing.attempts + 1 : 1;
    this.write(this.pendingExternalKey, {
      version: 1,
      target,
      source,
      savedAt: Date.now(),
      attempts,
    } satisfies PendingExternalNavigation);
  }

  private clearPendingExternal(target: string): void {
    const pending = this.validPendingExternal();
    if (!pending || pending.target === target) this.remove(this.pendingExternalKey);
  }

  private validPendingExternal(): PendingExternalNavigation | null {
    const pending = this.read<PendingExternalNavigation>(this.pendingExternalKey);
    if (!pending || pending.version !== 1 || Date.now() - pending.savedAt > this.pendingTtlMs) {
      this.remove(this.pendingExternalKey);
      return null;
    }
    return this.safeExternalTarget(pending.target) ? pending : null;
  }

  private safeRestorableRoute(rawRoute: string): string | null {
    const route = this.normalizedInternalRoute(rawRoute, true);
    if (!route) return null;
    const pathname = route.split(/[?#]/, 1)[0].toLowerCase();
    if (
      pathname === '/'
      || pathname.startsWith('/loader')
      || pathname.startsWith('/login')
      || pathname.startsWith('/register')
      || pathname.startsWith('/waiting-page')
      || pathname.startsWith('/verification')
      || pathname.startsWith('/create-publication')
      || pathname.startsWith('/chime-contact')
      // Embedded browser and checkout-detail surfaces depend on transient
      // navigation inputs. Reopening a blank or partially redacted instance
      // is less useful than returning to the main application.
      || pathname.startsWith('/inline-bowsing')
      || (pathname === '/stockkeepingunit' && !new URL(route, 'https://laniakea.tv').searchParams.get('product_id'))
      || /(?:checkout|purchase|payment|pago|recarga|callback|apple-tv\/sync)/i.test(pathname)
    ) return null;
    return route;
  }

  private readStoredRoute(): StoredRoute | null {
    const stored = this.read<StoredRoute>(this.lastRouteKey);
    if (!stored || (stored.version !== 1 && stored.version !== 2)) return null;
    return stored;
  }

  private safeBackRoute(rawCandidate: string | undefined, currentRoute: string, authenticated: boolean): string {
    const candidate = rawCandidate ? this.safeRestorableRoute(rawCandidate) : null;
    if (
      candidate
      && !this.sameRoute(candidate, currentRoute)
      && (authenticated || this.isPublicRoute(candidate))
    ) {
      return candidate;
    }
    return this.mainNavigationRoute;
  }

  private sameRoute(first: string, second: string): boolean {
    const firstPath = first.split(/[?#]/, 1)[0].toLowerCase();
    const secondPath = second.split(/[?#]/, 1)[0].toLowerCase();
    return !!firstPath && firstPath === secondPath;
  }

  private writeCurrentHistoryBackContext(route: string, fallback: string): void {
    if (typeof history === 'undefined') return;
    try {
      const currentState = history.state && typeof history.state === 'object' ? history.state : {};
      history.replaceState({
        ...currentState,
        [this.restoredBackStateKey]: { version: 1, route, fallback },
      }, '', location.href);
    } catch {
      // Browser history is an enhancement. The startup stack still provides a
      // physical fallback when a WebView disallows replacing history state.
    }
  }

  private currentHistoryBackContext(): { route: string; fallback: string } | null {
    if (typeof history === 'undefined') return null;
    const context = history.state?.[this.restoredBackStateKey];
    if (
      !context
      || context.version !== 1
      || typeof context.route !== 'string'
      || typeof context.fallback !== 'string'
    ) return null;
    return context;
  }

  private safeExternalTarget(rawTarget: string): string | null {
    return this.normalizedInternalRoute(rawTarget, false);
  }

  private normalizedInternalRoute(rawRoute: string, stripSensitiveValues: boolean): string | null {
    try {
      const parsed = new URL(rawRoute || '/', 'https://laniakea.tv');
      if (!parsed.pathname.startsWith('/') || parsed.pathname.startsWith('//')) return null;
      if (stripSensitiveValues) {
        Array.from(parsed.searchParams.keys()).forEach(key => {
          if (this.isSensitiveQueryKey(key)) parsed.searchParams.delete(key);
        });
      }
      const query = parsed.searchParams.toString();
      return `${parsed.pathname || '/'}${query ? `?${query}` : ''}${parsed.hash || ''}`;
    } catch {
      return null;
    }
  }

  private hasSensitiveQuery(rawRoute: string): boolean {
    try {
      const parsed = new URL(rawRoute || '/', 'https://laniakea.tv');
      return Array.from(parsed.searchParams.keys()).some(key => this.isSensitiveQueryKey(key));
    } catch {
      return true;
    }
  }

  private isSensitiveQueryKey(key: string): boolean {
    return /(?:token|secret|password|credential|authorization|signature|client_secret|payment_intent|nonce|session|code|state|email|phone|address)/i.test(key);
  }

  private isPublicRoute(route: string): boolean {
    const path = route.split(/[?#]/, 1)[0].toLowerCase();
    return [
      '/mycelium',
      '/home/ecosystem-page',
      '/feex-wholesale',
      '/feex',
      '/profile',
      '/search',
      '/explora',
      '/lk-music',
      '/lk-film-studios',
      '/earth',
      '/eventos-eclipse-view',
      '/hospedaje-earth-view',
    ].some(prefix => path === prefix || path.startsWith(`${prefix}/`));
  }

  private pruneRecentTargets(): void {
    const expiry = Date.now() - 5000;
    this.recentExternalTargets.forEach((handledAt, target) => {
      if (handledAt < expiry) this.recentExternalTargets.delete(target);
    });
  }

  private safeNamespace(namespace: string): string {
    return String(namespace || 'default').replace(/[^a-zA-Z0-9_.:-]/g, '_').slice(0, 80);
  }

  private read<T>(key: string): T | null {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null') as T | null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // State restoration is an optimization, never a startup dependency.
    }
  }

  private remove(key: string): void {
    try { localStorage.removeItem(key); } catch { /* best effort */ }
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }
}
